import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useFontSize } from '../contexts/FontSizeContext';
import { useDisplay } from '../contexts/DisplayContext';
import { apiService } from '../services/apiService';
import notificationService from '../services/notificationService';
import { CONSTANTS } from '../utils/constants';
import TransferOwnershipModal from './TransferOwnershipModal';
import './UltraModernProfile.css';
import { 
  User, 
  Shield, 
  Calendar,
  ArrowLeft,
  Globe,
  List,
  Bell,
  Database,
  TrendingUp,
  CheckSquare,
  Award,
  Users,
  Star,
  Plus,
  Monitor,
  Smartphone,
  Mail,
  MessageSquare,
  Share2,
  RefreshCw,
  CheckCircle,
  Eye,
  Play,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ShieldOff,
  UserCheck,
  Ban,
  Trash2,
  AlertTriangle,
  Clock,
  ChevronDown,
  BarChart3,
  Package,
  Lock,
  Edit2,
  X,
  Type
} from 'lucide-react';

const SettingsPage = ({ onBack }) => {
  const { user, updateUser } = useAuth();
  const { t, uiLanguage, setUILanguage, getAllCategories } = useLanguage();
  const { fontSize, updateFontSize, getFontSizes, getFontSizeLabel } = useFontSize();
  const { globalShowCategories, updateGlobalShowCategories, globalShowQuickAddButtons, updateGlobalShowQuickAddButtons } = useDisplay();
  
  // Main settings state
  const [activeSection, setActiveSection] = useState(() => {
    // Restore active section from localStorage on refresh
    return localStorage.getItem(CONSTANTS.SETTINGS_SECTION_KEY) || CONSTANTS.DEFAULT_SECTION;
  });
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    totalLists: 0,
    totalItems: 0,
    itemsChecked: 0,
    itemsCompleted: 0, // Total completion actions
    sharedLists: 0,
    averageProgress: 0,
    itemsPerList: 0,
    mostProductiveList: 'N/A'
  });
  const [newCategory, setNewCategory] = useState('');
  const [forceRender, setForceRender] = useState(0); // Force re-render trigger
  const [userDataLoaded, setUserDataLoaded] = useState(false); // Track if user data is loaded
  const [isUserAdmin, setIsUserAdmin] = useState(false); // Track admin status more reliably
  
  // Local categories state to avoid updateUser issues
  // Initialize with starter categories to ensure they're always visible
  const [localCategories, setLocalCategories] = useState([
    t('categories.Groceries', 'Groceries'),
    t('categories.Household', 'Household'), 
    t('categories.Personal Care', 'Personal Care'),
    t('categories.Health & Beauty', 'Health & Beauty'),
    t('categories.Electronics', 'Electronics'),
    t('categories.Clothing', 'Clothing')
  ]);
  
  // Initialize categories on component mount
  useEffect(() => {
    const initializeCategories = async () => {
      try {
        if (user?.customCategories && user.customCategories.length > 0) {
          // User has specific categories - use those instead of defaults
          const validCategories = user.customCategories.filter(cat => 
            typeof cat === CONSTANTS.DATA_TYPES.STRING && cat.trim().length > 0
          );
          setLocalCategories(validCategories);
        } else {
          // Get fresh user data to check for categories
          const freshUser = await apiService.getCurrentUser();
          if (freshUser?.customCategories && freshUser.customCategories.length > 0) {
            const validCategories = freshUser.customCategories.filter(cat => 
              typeof cat === CONSTANTS.DATA_TYPES.STRING && cat.trim().length > 0
            );
            setLocalCategories(validCategories);
          } else {
            // User has no categories - keep the starter categories we already have
            // Sync starter categories to the server
            for (const category of localCategories) {
              try {
                await apiService.addCustomCategory(category);
              } catch (error) {
                // Category might already exist
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize categories:', error);
      }
    };
    
    initializeCategories();
  }, []); // Run only once on mount

  // Modal state
  const [modal, setModal] = useState({ show: false, type: CONSTANTS.MODAL_TYPES.ALERT, title: '', message: '', onConfirm: null, onCancel: null });

  // Admin sections state
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showSystemStats, setShowSystemStats] = useState(false);
  const [showListManagement, setShowListManagement] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Admin panel state (integrated from AdminPanel)
  const [adminStats, setAdminStats] = useState(null);
  const [adminLists, setAdminLists] = useState([]);
  const [adminError, setAdminError] = useState(null);

  // Data retention state
  const [showDataRetention, setShowDataRetention] = useState(false);
  const [retentionStats, setRetentionStats] = useState(null);
  const [retentionConfig, setRetentionConfig] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);

  // List view modal state
  const [viewListModal, setViewListModal] = useState({ show: false, list: null });
  const [viewListDetails, setViewListDetails] = useState(null);
  const [viewListLoading, setViewListLoading] = useState(false);

  // Transfer ownership modal state
  const [transferOwnershipModal, setTransferOwnershipModal] = useState({ show: false, list: null });

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [oldPassword, setOldPassword] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [changePassword, setChangePassword] = useState(false); // Toggle for password change section

  // Sync local categories with user data (only if user has more categories than we do)
  useEffect(() => {
    if (user?.customCategories && Array.isArray(user.customCategories)) {
      const validCategories = user.customCategories.filter(cat => 
        typeof cat === CONSTANTS.DATA_TYPES.STRING && cat.trim().length > 0
      );
      
      // Only sync if user has more categories than we currently show
      if (validCategories.length > localCategories.length) {
        setLocalCategories(validCategories);
      }
    }
  }, [user?.customCategories, localCategories.length]);

  // ESC key support for modals
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (modal.show && modal.onCancel) {
          modal.onCancel();
        } else if (transferOwnershipModal.show) {
          closeTransferOwnershipModal();
        } else if (viewListModal.show) {
          setViewListModal({ show: false, list: null });
        }
      }
    };

    if (modal.show || transferOwnershipModal.show || viewListModal.show) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [modal.show, modal.onCancel, transferOwnershipModal.show, viewListModal.show]);

  // Force categories refresh when switching to categories section
  useEffect(() => {
    if (activeSection === CONSTANTS.SECTIONS.CATEGORIES && user) {
      // If localCategories is empty but user has categories, sync them
      if (user?.customCategories && Array.isArray(user.customCategories) && localCategories.length === 0) {
        const validCategories = user.customCategories.filter(cat => 
          typeof cat === CONSTANTS.DATA_TYPES.STRING && cat.trim().length > 0
        );
        setLocalCategories(validCategories);
      }
    }
  }, [activeSection, user, localCategories.length]);

  useEffect(() => {
    loadUserStats();
    // Only refresh user data if we don't have a complete user object
    // This prevents overwriting the admin status that's already loaded
    const refreshUserData = async () => {
      // Always refresh user data to ensure we have the latest admin status
      // Skip refresh only if we have complete data AND admin status is explicitly defined
      if (user && user.id && user.hasOwnProperty(CONSTANTS.PROPERTIES.IS_ADMIN) && user.isAdmin !== undefined) {
        console.log('🔍 User data already complete, admin status:', user.isAdmin);
        setUserDataLoaded(true);
        return;
      }
      
      console.log('🔄 Refreshing user data, current user:', user);
      
      try {
        const updatedUser = await apiService.getCurrentUser();
        console.log('🔄 Fresh user data received:', updatedUser);
        
        if (updatedUser) {
          updateUser(updatedUser);
          setUserDataLoaded(true);
          console.log('✅ User data updated, admin status:', updatedUser.isAdmin);
        } else {
          setUserDataLoaded(true);
        }
      } catch (error) {
        console.error('❌ Failed to refresh user data:', error);
        setUserDataLoaded(true); // Mark as loaded even if failed
      }
    };
    refreshUserData();
  }, [updateUser]);

  // Auto-refresh stats when page becomes visible or focus is regained
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && activeSection === CONSTANTS.SECTIONS.PROFILE) {
        loadUserStats();
      }
    };

    const handleFocus = () => {
      if (activeSection === CONSTANTS.SECTIONS.PROFILE) {
        loadUserStats();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [activeSection]);

  // Modal helpers
  const showAlert = (message, title = t('alert', 'Alert')) => {
    setModal({
      show: true,
      type: CONSTANTS.MODAL_TYPES.ALERT,
      title,
      message,
      onConfirm: () => setModal({ show: false, type: CONSTANTS.MODAL_TYPES.ALERT, title: '', message: '', onConfirm: null, onCancel: null }),
      onCancel: null
    });
  };

  const showConfirm = (message, onConfirm, title = t('confirm', 'Confirm')) => {
    setModal({
      show: true,
      type: CONSTANTS.MODAL_TYPES.CONFIRM,
      title,
      message,
      onConfirm: () => {
        setModal({ show: false, type: CONSTANTS.MODAL_TYPES.ALERT, title: '', message: '', onConfirm: null, onCancel: null });
        onConfirm();
      },
      onCancel: () => setModal({ show: false, type: CONSTANTS.MODAL_TYPES.ALERT, title: '', message: '', onConfirm: null, onCancel: null })
    });
  };

  useEffect(() => {
    // Force re-render when user changes (including custom categories)
    setForceRender(prev => prev + 1);
  }, [user?.customCategories, user, userDataLoaded]);

  // Additional useEffect to watch for user admin status changes
  useEffect(() => {
    // Force a re-render when admin status changes
    if (user?.isAdmin !== undefined) {
      setForceRender(prev => prev + 1);
    }
  }, [user?.isAdmin, user]);

  // Persist active section to localStorage
  useEffect(() => {
    localStorage.setItem(CONSTANTS.SETTINGS_SECTION_KEY, activeSection);
  }, [activeSection]);

  // Load admin data when admin section is accessed
  useEffect(() => {
    if (activeSection === CONSTANTS.SECTIONS.ADMIN && user?.isAdmin) {
      loadAdminData();
    }
    // Reload stats when switching to profile section
    if (activeSection === CONSTANTS.SECTIONS.PROFILE) {
      loadUserStats();
    }
  }, [activeSection, user?.isAdmin]);

  const loadUserStats = async () => {
    try {
      setLoading(true);
      
      const stats = await apiService.getUserStats();
      setUserStats(stats);
    } catch (error) {
      console.error('Failed to load user stats:', error);
      
      // Check if it's a network error
      if (error.message.includes(CONSTANTS.ERROR_PATTERNS.FETCH)) {
        console.log(t('networkErrorBackendNotRunning', 'Network error - backend might not be running'));
      }
      
      // Fallback: Calculate stats from existing lists
      try {
        const lists = await apiService.getLists();
        
        let totalItems = 0;
        let itemsChecked = 0;
        let totalProgress = 0;
        let listsWithProgress = 0;
        
        lists.forEach(list => {
          const items = list.items || [];
          totalItems += items.length;
          const checkedItems = items.filter(item => item.checked).length;
          itemsChecked += checkedItems;
          
          if (items.length > 0) {
            totalProgress += (checkedItems / items.length) * 100;
            listsWithProgress++;
          }
        });
        
        const averageProgress = listsWithProgress > 0 ? Math.round(totalProgress / listsWithProgress) : 0;
        const itemsPerList = lists.length > 0 ? Math.round(totalItems / lists.length) : 0;
        
        // Find most productive list
        let mostProductiveList = t('notAvailable', 'N/A');
        let highestCompletion = 0;
        lists.forEach(list => {
          const items = list.items || [];
          if (items.length > 0) {
            const completion = (items.filter(item => item.checked).length / items.length) * 100;
            if (completion > highestCompletion) {
              highestCompletion = completion;
              mostProductiveList = list.name;
            }
          }
        });
        
        const fallbackStats = {
          totalLists: lists.length,
          totalItems,
          itemsChecked,
          itemsCompleted: 0, // Can't calculate completions from frontend
          sharedLists: 0, // Can't calculate shared lists from frontend
          averageProgress,
          itemsPerList,
          mostProductiveList
        };
        
        setUserStats(fallbackStats);
      } catch (fallbackError) {
        console.error('Fallback stats calculation failed:', fallbackError);
        // Set default values if everything fails
        setUserStats({
          totalLists: 0,
          totalItems: 0,
          itemsChecked: 0,
          itemsCompleted: 0,
          sharedLists: 0,
          averageProgress: 0,
          itemsPerList: 0,
          mostProductiveList: t('notAvailable', 'N/A')
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Make stats refresh available globally
  useEffect(() => {
    // Store refresh function globally so other components can trigger stats refresh
    window.refreshUserStats = loadUserStats;
    
    return () => {
      // Clean up on unmount
      delete window.refreshUserStats;
    };
  }, []);

  // Profile editing handlers
  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      showAlert(t('nameRequired', 'Name is required.'));
      return;
    }

    if (changePassword) {
      if (!oldPassword.trim()) {
        showAlert(t('oldPasswordRequired', 'Current password is required to change password.'));
        return;
      }
      
      if (!editPassword.trim()) {
        showAlert(t('newPasswordRequired', 'New password is required.'));
        return;
      }
      
      if (editPassword.length < 6) {
        showAlert(t('passwordTooShort', 'New password must be at least 6 characters long.'));
        return;
      }
      
      if (editPassword !== confirmPassword) {
        showAlert(t('passwordsDoNotMatch', 'New passwords do not match.'));
        return;
      }
    }

    try {
      setProfileSaving(true);
      const updateData = { name: editName };
      
      if (changePassword) {
        updateData.oldPassword = oldPassword;
        updateData.password = editPassword;
      }

      const response = await apiService.updateProfile(updateData);
      if (response.success) {
        await updateUser(response.user);
        setEditingProfile(false);
        setChangePassword(false);
        setOldPassword('');
        setEditPassword('');
        setConfirmPassword('');
        showAlert(t('profileUpdated', 'Profile updated successfully.'), t('success', 'Success'));
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      showAlert(error.message || t('profileUpdateFailed', 'Failed to update profile. Please try again.'), t('error', 'Error'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingProfile(false);
    setEditName(user?.name || '');
    setChangePassword(false);
    setOldPassword('');
    setEditPassword('');
    setConfirmPassword('');
  };

  // Category management handlers
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    const newCategoryName = newCategory.trim();
    
    // Check if category already exists (including standard categories)
    const standardCategories = [
      'Food', 'Household', 'Personal Care', 'Work/Office', 'Shopping', 'Other'
    ];
    
    const allExistingCategories = [...standardCategories, ...localCategories];
    
    if (allExistingCategories.some(cat => cat.toLowerCase() === newCategoryName.toLowerCase())) {
      showAlert(
        t('categoryAlreadyExists', `The category "${newCategoryName}" already exists.`), 
        t('duplicateCategory', 'Duplicate Category')
      );
      setNewCategory('');
      return;
    }

    // Optimistically update local state
    setLocalCategories(prev => [...prev, newCategoryName]);
    setNewCategory('');

    try {
      // Call API
      await apiService.addCustomCategory(newCategoryName);
      
      // Refresh user data in background (but don't wait for it)
      apiService.getCurrentUser().then(updatedUser => {
        // Don't call updateUser here as it might cause conflicts
      });
      
    } catch (error) {
      console.error('❌ Failed to add category:', error);
      // Revert optimistic update
      setLocalCategories(prev => prev.filter(cat => cat !== newCategoryName));
      showAlert(
        t('failedToAddCategory', 'Failed to add category. Please try again.'),
        t('error', 'Error')
      );
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    // Show confirmation modal instead of browser confirm
    showConfirm(
      t('confirmDeleteCategory', `Are you sure you want to delete the category "${categoryName}"?`),
      async () => {
        // This function runs if user confirms
        
        // Optimistically update local state
        const originalCategories = [...localCategories];
        setLocalCategories(prev => prev.filter(cat => cat !== categoryName));

        try {
          await apiService.deleteCustomCategory(categoryName);
          
          // Refresh user data in background (but don't wait for it)
          apiService.getCurrentUser().then(updatedUser => {
            // Don't call updateUser here as it might cause conflicts
          });
          
        } catch (error) {
          console.error('❌ Failed to delete category:', error);
          // Revert optimistic update
          setLocalCategories(originalCategories);
          showAlert(
            t('failedToDeleteCategory', 'Failed to delete category. Please try again.'),
            t('error', 'Error')
          );
        }
      },
      t('confirmDelete', 'Confirm Delete')
    );
  };

  const handleToggleDefaultCategory = async (categoryKey) => {
    try {
      const hiddenCategories = user?.hiddenCategories || [];
      const isCurrentlyHidden = hiddenCategories.includes(categoryKey);
      
      let updatedHiddenCategories;
      if (isCurrentlyHidden) {
        // Show the category
        updatedHiddenCategories = hiddenCategories.filter(cat => cat !== categoryKey);
      } else {
        // Hide the category
        updatedHiddenCategories = [...hiddenCategories, categoryKey];
      }
      
      await updateUser({ hiddenCategories: updatedHiddenCategories });
    } catch (error) {
      console.error('Failed to toggle category visibility:', error);
      showAlert(t('failedToUpdateCategory', 'Failed to update category. Please try again.'), t('error', 'Error'));
    }
  };

  const handleShowAllCategories = async () => {
    try {
      await updateUser({ hiddenCategories: [] });
    } catch (error) {
      console.error('Failed to show all categories:', error);
      showAlert(t('failedToUpdateCategory', 'Failed to update categories. Please try again.'), t('error', 'Error'));
    }
  };

  // Admin panel handlers (integrated from AdminPanel)
  const loadAdminData = async () => {
    if (!user?.isAdmin) return;
    
    try {
      setUsersLoading(true);
      const [statsData, usersData, listsData] = await Promise.all([
        apiService.getAdminStats().catch(() => ({ overview: {}, recentActivity: {}, systemHealth: {} })),
        apiService.getAllUsers(),
        apiService.getAllLists().catch(() => [])
      ]);
      const processedUsers = Array.isArray(usersData) ? usersData : usersData.users || [];
      setAdminStats(statsData);
      setAllUsers(processedUsers);
      setAdminLists(Array.isArray(listsData) ? listsData : listsData.lists || []);
      setAdminError(null);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      setAdminError('Failed to load admin data');
      showAlert(t('failedToLoadAdminData', 'Failed to load admin data. Please try again.'), t('error', 'Error'));
    } finally {
      setUsersLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!user?.isAdmin) return;
    
    const userToDelete = allUsers.find(u => u.id === userId);
    if (!userToDelete) return;

    const confirmMessage = t('confirmDeleteUser', 'Are you sure you want to delete user "{0}" ({1})? This will also delete all their lists and data.').replace('{0}', userToDelete.name).replace('{1}', userToDelete.email);
    showConfirm(
      confirmMessage,
      async () => {
        try {
          await apiService.deleteUser(userId);
          setAllUsers(prev => prev.filter(u => u.id !== userId));
          await loadAdminData(); // Refresh all admin data
          showAlert(t('userDeleted', 'User deleted successfully.'), t('success', 'Success'));
        } catch (error) {
          console.error('Failed to delete user:', error);
          showAlert(t('failedToDeleteUser', 'Failed to delete user. Please try again.'), t('error', 'Error'));
        }
      },
      t('deleteUser', 'Delete User')
    );
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    if (!user?.isAdmin) return;
    
    const userToUpdate = allUsers.find(u => u.id === userId);
    if (!userToUpdate) return;

    const newStatus = !currentStatus;
    const action = newStatus ? 'grant admin privileges to' : 'remove admin privileges from';
    const confirmMessage = t('confirmToggleAdmin', 'Are you sure you want to {0} "{1}"?').replace('{0}', action).replace('{1}', userToUpdate.name);

    showConfirm(
      confirmMessage,
      async () => {
        try {
          await apiService.updateUserAdmin(userId, newStatus);
          setAllUsers(prev => prev.map(u => 
            u.id === userId ? { ...u, isAdmin: newStatus } : u
          ));
          showAlert(t('userUpdated', 'User updated successfully.'), t('success', 'Success'));
        } catch (error) {
          console.error('Failed to update user admin status:', error);
          showAlert(t('failedToUpdateUser', 'Failed to update user admin status. Please try again.'), t('error', 'Error'));
        }
      },
      t('updateUserRole', 'Update User Role')
    );
  };

  const handleToggleBlocked = async (userId, currentStatus) => {
    if (!user?.isAdmin) return;
    
    // First refresh the user data to ensure we have the latest users from backend
    await loadAdminData();
    
    // Small delay to ensure state updates
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const userToUpdate = allUsers.find(u => u.id === userId);
    if (!userToUpdate) {
      showAlert(t('userNotFound', 'User not found. The user list has been refreshed.'), t('error', 'Error'));
      return;
    }

    const newStatus = !currentStatus;
    const action = newStatus ? 'block' : 'unblock';
    const confirmMessage = t('confirmToggleBlocked', 'Are you sure you want to {0} "{1}"?').replace('{0}', action).replace('{1}', userToUpdate.name);

    showConfirm(
      confirmMessage,
      async () => {
        try {
          console.log('Attempting to toggle blocked status:', { userId, currentStatus, newStatus });
          console.log('All users in frontend:', allUsers.map(u => ({ id: u.id, name: u.name, isBlocked: u.isBlocked })));
          const response = await apiService.toggleUserBlocked(userId, newStatus);
          console.log('Block toggle response:', response);
          
          // Refresh the user list from server to ensure we have fresh data
          await loadAdminData();
          
          showAlert(t('userUpdated', 'User updated successfully.'), t('success', 'Success'));
        } catch (error) {
          console.error('Failed to update user blocked status:', error);
          console.error('Error details:', error.message, error.stack);
          showAlert(t('failedToUpdateUser', 'Failed to update user blocked status. Please try again.'), t('error', 'Error'));
        }
      },
      t('updateUserStatus', 'Update User Status')
    );
  };

  // Utility functions for admin panel (from AdminPanel)
  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatMemory = (bytes) => {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Data retention functions
  const loadDataRetentionData = async () => {
    setRetentionLoading(true);
    try {
      const [statsData, configData] = await Promise.all([
        apiService.getDataRetentionStats(),
        apiService.getDataRetentionConfig()
      ]);
      setRetentionStats(statsData);
      setRetentionConfig(configData);
    } catch (err) {
      console.error('Error loading data retention data:', err);
      setAdminError(t('failedToLoadDataRetention', 'Failed to load data retention information'));
    } finally {
      setRetentionLoading(false);
    }
  };

  const handleDataRetentionCleanup = async () => {
    showConfirm(
      t('confirmDataRetentionCleanup', 'Are you sure you want to run data retention cleanup? This will permanently delete old data according to the retention policy.'),
      async () => {
        try {
          setCleanupLoading(true);
          const results = await apiService.runDataRetentionCleanup();
          setCleanupResults(results);
          
          // Reload all data to reflect changes
          await Promise.all([loadAdminData(), loadDataRetentionData()]);
          
          showAlert(t('dataRetentionCleanupCompleted', 'Data retention cleanup completed successfully!'), t('success', 'Success'));
        } catch (err) {
          console.error('Error running cleanup:', err);
          showAlert(t('failedToRunDataRetentionCleanup', 'Failed to run data retention cleanup. Please try again.'), t('error', 'Error'));
        } finally {
          setCleanupLoading(false);
        }
      },
      t('confirmDataRetentionCleanupTitle', 'Confirm Data Cleanup')
    );
  };

  const formatDays = (days) => {
    if (days === 1) return t('oneDay', '1 day');
    if (days < 30) return t('daysCount', '{0} days').replace('{0}', days);
    if (days < 365) return t('monthsCount', '{0} months').replace('{0}', Math.round(days / 30));
    return t('yearsCount', '{0} years').replace('{0}', Math.round(days / 365));
  };

  // List Management Handlers
  const handleViewList = async (listId) => {
    console.log('handleViewList called with listId:', listId);
    const list = adminLists.find(l => l.id === listId);
    if (!list) {
      showAlert(t('listNotFound', 'List not found'), t('error', 'Error'));
      return;
    }

    console.log('Found list:', list);

    // Show modal immediately with basic info
    setViewListModal({ show: true, list });
    setViewListLoading(true);
    setViewListDetails(null);

    try {
      // Fetch detailed list information
      console.log('Calling apiService.getListDetailsAsAdmin with listId:', listId);
      const details = await apiService.getListDetailsAsAdmin(listId);
      console.log('Received list details:', details);
      setViewListDetails(details);
    } catch (error) {
      console.error('Failed to fetch list details:', error);
      console.error('Error details:', error.message, error.stack);
      showAlert(t('failedToLoadListDetails', 'Failed to load list details. Please try again.'), t('error', 'Error'));
    } finally {
      setViewListLoading(false);
    }
  };

  const closeViewListModal = () => {
    setViewListModal({ show: false, list: null });
    setViewListDetails(null);
    setViewListLoading(false);
  };

  const handleEditList = (listId) => {
    // TODO: Implement edit list modal
    console.log('Edit list:', listId);
    showAlert(t('listEditingComingSoon', 'List editing - coming soon!'), t('info', 'Info'));
  };

  const handleToggleListBlock = async (listId, shouldBlock) => {
    if (!user?.isAdmin) return;
    
    const list = adminLists.find(l => l.id === listId);
    if (!list) {
      showAlert(t('listNotFound', 'List not found'), t('error', 'Error'));
      return;
    }

    const action = shouldBlock ? t('block', 'block') : t('unblock', 'unblock');
    const actionTitle = shouldBlock ? t('blockList', 'Block List') : t('unblockList', 'Unblock List');
    
    let confirmMessage;
    if (shouldBlock) {
      confirmMessage = t('confirmBlockListDetailed', 'Are you sure you want to block "{0}"?\n\nWhen blocked:\n• Users will no longer see this list in their lists\n• Users cannot access or modify this list\n• Only admins can view and manage blocked lists\n• The list can be unblocked at any time to restore access').replace('{0}', list.name);
    } else {
      confirmMessage = t('confirmUnblockListDetailed', 'Are you sure you want to unblock "{0}"?\n\nWhen unblocked:\n• Users will be able to see and access this list again\n• All previous sharing settings will be restored\n• Users can continue using the list normally').replace('{0}', list.name);
    }

    showConfirm(
      confirmMessage,
      async () => {
        try {
          await apiService.toggleListBlock(listId, shouldBlock);
          await loadAdminData(); // Refresh the list
          const statusText = shouldBlock ? t('blocked', 'blocked') : t('unblocked', 'unblocked');
          showAlert(
            t('listBlockedSuccessfully', 'List "{0}" has been {1} successfully.').replace('{0}', list.name).replace('{1}', statusText), 
            t('success', 'Success')
          );
        } catch (error) {
          console.error('Failed to toggle list block status:', error);
          showAlert(
            t('failedToToggleListBlock', 'Failed to {0} list. Please try again.').replace('{0}', action), 
            t('error', 'Error')
          );
        }
      },
      actionTitle
    );
  };

  const handleTransferOwnership = (listId) => {
    if (!user?.isAdmin) return;
    
    const list = adminLists.find(l => l.id === listId);
    if (!list) {
      showAlert(t('listNotFound', 'List not found'), t('error', 'Error'));
      return;
    }

    setTransferOwnershipModal({ show: true, list });
  };

  const closeTransferOwnershipModal = () => {
    setTransferOwnershipModal({ show: false, list: null });
  };

  const handleTransferComplete = async (listId, newOwner) => {
    // Refresh admin data to reflect the ownership change
    await loadAdminData();
    showAlert(
      t('ownershipTransferredSuccessfully', 'List ownership has been transferred to {0} successfully.').replace('{0}', newOwner.name),
      t('success', 'Success')
    );
  };

  const handleDeleteList = async (listId, listName) => {
    if (!user?.isAdmin) return;

    const confirmMessage = t('confirmDeleteListAdmin', 'Are you sure you want to permanently delete "{0}"? This action cannot be undone and will delete all items in the list.').replace('{0}', listName);

    showConfirm(
      confirmMessage,
      async () => {
        try {
          await apiService.deleteListAsAdmin(listId);
          await loadAdminData(); // Refresh the list
          showAlert(
            t('listDeletedSuccessfully', 'List "{0}" has been deleted successfully.').replace('{0}', listName), 
            t('success', 'Success')
          );
        } catch (error) {
          console.error('Failed to delete list:', error);
          showAlert(
            t('failedToDeleteList', 'Failed to delete list. Please try again.'), 
            t('error', 'Error')
          );
        }
      },
      t('deleteList', 'Delete List')
    );
  };

  const filteredUsers = allUsers.filter(u => {
    const nameMatch = u.name && u.name.toLowerCase().includes(userSearchQuery.toLowerCase());
    const emailMatch = u.email && u.email.toLowerCase().includes(userSearchQuery.toLowerCase());
    return nameMatch || emailMatch;
  });

  // Update admin status when user changes
  useEffect(() => {
    // Handle both boolean and integer values from database
    if (user?.isAdmin) {
      setIsUserAdmin(true);
      console.log('🔍 Setting isUserAdmin to true, user.isAdmin:', user.isAdmin);
    } else if (user?.isAdmin === false || user?.isAdmin === 0) {
      setIsUserAdmin(false);
      console.log('🔍 Setting isUserAdmin to false, user.isAdmin:', user.isAdmin);
    }
    // If user.isAdmin is undefined, keep the previous state
  }, [user?.isAdmin]);

  // Settings navigation - recalculated when user changes
  const settingsSections = useMemo(() => {
    console.log('Recalculating settings sections, user.isAdmin:', user?.isAdmin, 'stored isUserAdmin:', isUserAdmin);
    console.log('Full user object in settingsSections:', user);
    // Convert isAdmin to boolean properly - handle both boolean and integer values
    const adminStatus = Boolean(user?.isAdmin) || isUserAdmin;
    console.log('🔍 Admin status calculated:', adminStatus, 'from user.isAdmin:', user?.isAdmin, 'isUserAdmin:', isUserAdmin);
    return [
      { id: 'profile', name: t('profile', 'Profile'), icon: User },
      { id: 'languages', name: t('languages', 'Languages'), icon: Globe },
      { id: 'categories', name: t('categoriesLabel', 'Categories'), icon: List },
      { id: 'notifications', name: t('notifications', 'Notifications'), icon: Bell },
      { id: 'data', name: t('dataAndPrivacy', 'Data & Privacy'), icon: Database },
      ...(adminStatus ? [{ id: 'admin', name: t('administration', 'Administration'), icon: Shield }] : [])
    ];
  }, [user, user?.isAdmin, isUserAdmin, t]);

  // Debug log for admin status
  console.log('Settings page user object:', user);
  console.log('User isAdmin:', user?.isAdmin);
  console.log('Settings sections:', settingsSections.map(s => s.id));

  // Section renderers
  const renderProfileSection = () => {
    // Handle date formatting safely
    const formatMemberSince = (createdAt) => {
      if (!createdAt) return t('unknown', 'Unknown');
      try {
        const date = new Date(createdAt);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      } catch {
        return t('unknown', 'Unknown');
      }
    };

    const getDaysActive = (createdAt) => {
      if (!createdAt) return 0;
      const created = new Date(createdAt);
      const now = new Date();
      return Math.floor((now - created) / (1000 * 60 * 60 * 24));
    };

    const getInitials = (name) => {
      if (!name) return 'U';
      return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
    };

    // Debug logging
    console.log('Profile section rendering with userStats:', userStats);
    console.log('User created at:', user.createdAt, 'Days active:', getDaysActive(user.createdAt));

    return (
      <div className="ultra-modern-profile-section">
        {/* Revolutionary Header */}
        <div className="profile-hero-banner">
          <div className="floating-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
          
          <div className="profile-hero-content">
            <div className="profile-avatar-section">
              <div className="avatar-container">
                <div className="avatar-ring"></div>
                <div className="avatar-inner">
                  <div className="avatar-gradient">
                    <span className="avatar-initials">{getInitials(user.name)}</span>
                  </div>
                  {user.isAdmin && (
                    <div className="admin-crown">
                      <Shield size={16} />
                    </div>
                  )}
                </div>
                <div className="status-indicator online"></div>
              </div>
              
              <div className="profile-quick-stats">
                <div className="stats-header">
                  <button 
                    className="stats-refresh-btn" 
                    onClick={loadUserStats}
                    disabled={loading}
                    title={t('refreshStats', 'Refresh statistics')}
                  >
                    <RefreshCw className={`icon ${loading ? 'spinning' : ''}`} size={14} />
                  </button>
                </div>
                <div className="stat-bubble">
                  <span className="stat-number">{userStats.totalLists || 0}</span>
                  <span className="stat-label">{t('lists', 'Lists')}</span>
                </div>
                <div className="stat-bubble">
                  <span className="stat-number">{userStats.totalItems || 0}</span>
                  <span className="stat-label">{t('items', 'Items')}</span>
                </div>
                <div className="stat-bubble">
                  <span className="stat-number">{userStats.itemsCompleted || 0}</span>
                  <span className="stat-label">{t('completed', 'Completed')}</span>
                </div>
                <div className="stat-bubble">
                  <span className="stat-number">{getDaysActive(user.createdAt)}</span>
                  <span className="stat-label">{t('days', 'Days')}</span>
                </div>
              </div>
            </div>

            <div className="profile-identity">
              <div className="name-section">
                <h1 className="user-name-hero">{user.name}</h1>
                <div className="user-role">
                  {user.isAdmin ? (
                    <div className="role-admin">
                      <Shield size={14} />
                      <span>{t('administrator', 'Administrator')}</span>
                    </div>
                  ) : (
                    <div className="role-member">
                      <User size={14} />
                      <span>{t('member', 'Member')}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="contact-info">
                <div className="email-display">
                  <div className="email-icon">📧</div>
                  <span className="email-text">{user.email}</span>
                </div>
                <div className="joined-date">
                  <div className="date-icon">🗓️</div>
                  <span>{t('joined', 'Joined')} {formatMemberSince(user.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Profile Cards */}
        <div className="profile-cards-grid">
          {/* Account Management Card */}
          <div className="profile-card account-card">
            <div className="card-header">
              <div className="card-icon">
                <User size={24} />
              </div>
              <div className="card-title">
                <h3>{t('accountSettings', 'Account Settings')}</h3>
                <p>{t('managePersonalInfo', 'Manage your personal information')}</p>
              </div>
            </div>
            
            <div className="card-content">
              {!editingProfile ? (
                <div className="account-preview">
                  <div className="info-row">
                    <span className="info-label">{t('displayName', 'Display Name')}</span>
                    <span className="info-value">{user.name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('emailAddress', 'Email Address')}</span>
                    <span className="info-value">{user.email}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('accountStatus', 'Account Status')}</span>
                    <span className="status-active">{t('statusActive', 'Active')}</span>
                  </div>
                </div>
              ) : (
                <div className="account-edit-form">
                  <div className="modern-input-group">
                    <input
                      id="editName"
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="modern-input"
                      placeholder=" "
                    />
                    <label htmlFor="editName" className="modern-label">{t('displayName', 'Display Name')}</label>
                  </div>

                  <div className="password-section">
                    <div className="password-toggle">
                      <span>{t('changePassword', 'Change Password')}</span>
                      <label className="neo-toggle">
                        <input
                          type="checkbox"
                          checked={changePassword}
                          onChange={(e) => setChangePassword(e.target.checked)}
                        />
                        <span className="neo-slider"></span>
                      </label>
                    </div>

                    {changePassword && (
                      <div className="password-fields">
                        <div className="modern-input-group">
                          <input
                            id="oldPassword"
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            className="modern-input"
                            placeholder=" "
                          />
                          <label htmlFor="oldPassword" className="modern-label">{t('currentPassword', 'Current Password')}</label>
                        </div>

                        <div className="modern-input-group">
                          <input
                            id="editPassword"
                            type="password"
                            value={editPassword}
                            onChange={(e) => setEditPassword(e.target.value)}
                            className="modern-input"
                            placeholder=" "
                          />
                          <label htmlFor="editPassword" className="modern-label">{t('newPassword', 'New Password')}</label>
                        </div>

                        <div className="modern-input-group">
                          <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="modern-input"
                            placeholder=" "
                          />
                          <label htmlFor="confirmPassword" className="modern-label">{t('confirmPassword', 'Confirm Password')}</label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="card-actions">
              {!editingProfile ? (
                <button 
                  onClick={() => setEditingProfile(true)}
                  className="neo-btn neo-btn-primary"
                >
                  <User size={16} />
                  {t('editProfile', 'Edit Profile')}
                </button>
              ) : (
                <div className="action-group">
                  <button
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                    className="neo-btn neo-btn-success"
                  >
                    {profileSaving ? (
                      <>
                        <div className="spinner"></div>
                        {t('saving', 'Saving...')}
                      </>
                    ) : (
                      <>
                        <CheckSquare size={16} />
                        {t('saveChanges', 'Save Changes')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="neo-btn neo-btn-secondary"
                  >
                    {t('cancel', 'Cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Activity Insights Card */}
          <div className="profile-card activity-card">
            <div className="card-header">
              <div className="card-icon activity-icon">
                <TrendingUp size={24} />
              </div>
              <div className="card-title">
                <h3>{t('yourActivity', 'Your Activity')}</h3>
                <p>{t('shoppingInsights', 'Shopping insights & progress')}</p>
              </div>
            </div>
            
            <div className="card-content">
              <div className="activity-metrics">
                {userStats.totalLists === 0 ? (
                  <div className="no-activity-message">
                    <div className="no-activity-icon">📝</div>
                    <h4>{t('startShoppingJourney', 'Start Your Shopping Journey!')}</h4>
                    <p>{t('createFirstListMessage', 'Create your first shopping list to see your activity stats here.')}</p>
                    <button 
                      className="neo-btn neo-btn-primary"
                      onClick={() => {
                        // Navigate to main app to create a list
                        window.location.href = '/';
                      }}
                      style={{ marginTop: '1rem' }}
                    >
                      <List size={16} />
                      {t('createFirstList', 'Create Your First List')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="metric-item">
                      <div className="metric-visual">
                        <div className="metric-circle lists">
                          <span>{userStats.totalLists || 0}</span>
                        </div>
                      </div>
                      <div className="metric-info">
                        <span className="metric-label">{t('shoppingLists', 'Shopping Lists')}</span>
                        <span className="metric-trend">+{userStats.sharedLists || 0} {t('shared', 'shared')}</span>
                      </div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-visual">
                        <div className="metric-circle items">
                          <span>{userStats.totalItems || 0}</span>
                        </div>
                      </div>
                      <div className="metric-info">
                        <span className="metric-label">{t('totalItems', 'Total Items')}</span>
                        <span className="metric-trend">{userStats.itemsChecked || 0} {t('completed', 'completed')}</span>
                      </div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-visual">
                        <div className="progress-ring">
                          <svg className="progress-circle" width="60" height="60">
                            <circle
                              cx="30"
                              cy="30"
                              r="25"
                              stroke="#e2e8f0"
                              strokeWidth="4"
                              fill="none"
                            />
                            <circle
                              cx="30"
                              cy="30"
                              r="25"
                              stroke="#667eea"
                              strokeWidth="4"
                              fill="none"
                              strokeDasharray={`${(userStats.averageProgress || 0) * 1.57} 157`}
                              strokeLinecap="round"
                              transform="rotate(-90 30 30)"
                            />
                          </svg>
                          <span className="progress-text">{userStats.averageProgress || 0}%</span>
                        </div>
                      </div>
                      <div className="metric-info">
                        <span className="metric-label">{t('avgProgress', 'Avg. Progress')}</span>
                        <span className="metric-trend">{t('efficiencyScore', 'efficiency score')}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* App Settings Card */}
          <div className="profile-card app-settings-card">
            <div className="card-header">
              <div className="card-icon">
                <Type size={24} />
              </div>
              <div className="card-title">
                <h3>{t('appSettings', 'App Settings')}</h3>
                <p>{t('fontSizeDescription', 'Adjust text size for better readability on your device')}</p>
              </div>
            </div>
            
            <div className="card-content">
              <div className="font-size-setting">
                <div className="setting-info">
                  <span className="setting-label">{t('fontSize', 'Font Size')}</span>
                  <span 
                    className="setting-current"
                    style={{ fontSize: getFontSizes().itemName }}
                  >
                    {t('current', 'Current')}: {getFontSizeLabel()}
                  </span>
                </div>
                <div className="font-size-controls">
                  <button 
                    onClick={() => updateFontSize('small')}
                    className={`font-size-btn ${fontSize === 'small' ? 'active' : ''}`}
                  >
                    {t('small', 'Small')}
                  </button>
                  <button 
                    onClick={() => updateFontSize('medium')}
                    className={`font-size-btn ${fontSize === 'medium' ? 'active' : ''}`}
                  >
                    {t('medium', 'Medium')}
                  </button>
                  <button 
                    onClick={() => updateFontSize('large')}
                    className={`font-size-btn ${fontSize === 'large' ? 'active' : ''}`}
                  >
                    {t('large', 'Large')}
                  </button>
                  <button 
                    onClick={() => updateFontSize('xlarge')}
                    className={`font-size-btn ${fontSize === 'xlarge' ? 'active' : ''}`}
                  >
                    {t('xlarge', 'X-Large')}
                  </button>
                  <button 
                    onClick={() => updateFontSize('huge')}
                    className={`font-size-btn ${fontSize === 'huge' ? 'active' : ''}`}
                  >
                    {t('huge', 'Huge')}
                  </button>
                </div>
              </div>
              
              <div className="display-setting" style={{ marginTop: '16px' }}>
                <div className="setting-info">
                  <span className="setting-label">{t('showCategories', 'Show Categories')}</span>
                  <span className="setting-current">
                    {t('current', 'Current')}: {globalShowCategories ? t('alwaysShow', 'Always Show') : t('alwaysHide', 'Always Hide')}
                  </span>
                </div>
                <p className="setting-description" style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px 0' }}>
                  {t('showCategoriesDescription', 'Show category headers in your shopping lists by default')}
                </p>
                <div className="display-controls" style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => updateGlobalShowCategories(true)}
                    className={`display-btn ${globalShowCategories ? 'active' : ''}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      backgroundColor: globalShowCategories ? '#007bff' : '#f8f9fa',
                      color: globalShowCategories ? 'white' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <Eye size={14} />
                    {t('alwaysShow', 'Always Show')}
                  </button>
                  <button 
                    onClick={() => updateGlobalShowCategories(false)}
                    className={`display-btn ${!globalShowCategories ? 'active' : ''}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      backgroundColor: !globalShowCategories ? '#007bff' : '#f8f9fa',
                      color: !globalShowCategories ? 'white' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <Eye size={14} style={{ opacity: 0.5 }} />
                    {t('alwaysHide', 'Always Hide')}
                  </button>
                </div>
              </div>

              <div className="display-setting" style={{ marginTop: '16px' }}>
                <div className="setting-info">
                  <span className="setting-label">{t('showQuickAddButtons', 'Show Quick Add Buttons')}</span>
                  <span className="setting-current">
                    {t('current', 'Current')}: {globalShowQuickAddButtons ? t('alwaysShow', 'Always Show') : t('alwaysHide', 'Always Hide')}
                  </span>
                </div>
                <p className="setting-description" style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px 0' }}>
                  {t('showQuickAddButtonsDescription', 'Show category buttons under the quick add text input')}
                </p>
                <div className="display-controls" style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => updateGlobalShowQuickAddButtons(true)}
                    className={`display-btn ${globalShowQuickAddButtons ? 'active' : ''}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      backgroundColor: globalShowQuickAddButtons ? '#007bff' : '#f8f9fa',
                      color: globalShowQuickAddButtons ? 'white' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <Eye size={14} />
                    {t('alwaysShow', 'Always Show')}
                  </button>
                  <button 
                    onClick={() => updateGlobalShowQuickAddButtons(false)}
                    className={`display-btn ${!globalShowQuickAddButtons ? 'active' : ''}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      backgroundColor: !globalShowQuickAddButtons ? '#007bff' : '#f8f9fa',
                      color: !globalShowQuickAddButtons ? 'white' : '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <Eye size={14} style={{ opacity: 0.5 }} />
                    {t('alwaysHide', 'Always Hide')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLanguageSection = () => (
    <div className="settings-section modern-language-section">
      <div className="section-header">
        <h3>{t('languages', 'Languages')}</h3>
        <p>{t('languageDescription', 'Set your interface language preference.')}</p>
      </div>

      <div className="language-options">
              <div 
                className={`language-option ${uiLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setUILanguage('en')}
              >
                <div className="language-flag">🇺🇸</div>
                <div className="language-details">
                  <span className="language-name">English</span>
                  <span className="language-native">English</span>
                </div>
                {uiLanguage === 'en' && (
                  <div className="language-check">
                    <CheckSquare size={20} />
                  </div>
                )}
              </div>
              
              <div 
                className={`language-option ${uiLanguage === 'no' ? 'active' : ''}`}
                onClick={() => setUILanguage('no')}
              >
                <div className="language-flag">🇳🇴</div>
                <div className="language-details">
                  <span className="language-name">Norwegian</span>
                  <span className="language-native">Norsk</span>
                </div>
                {uiLanguage === 'no' && (
                  <div className="language-check">
                    <CheckSquare size={20} />
                  </div>
                )}
              </div>
            </div>
    </div>
  );

  const renderCategoriesSection = () => {
    // Get user's categories (includes starter categories)
    let customCategories = [];
    if (Array.isArray(user?.customCategories)) {
      customCategories = user.customCategories.filter(cat => 
        typeof cat === CONSTANTS.DATA_TYPES.STRING && cat.trim().length > 0
      );
    }
    
    console.log('�️ Rendering categories section with:', customCategories.length, 'categories');
    
    return (
      <div className="settings-section modern-categories-section">
        <div className="section-header">
          <h3>{t('categoriesLabel', 'Categories')}</h3>
          <p>{t('categoriesDescription', 'Organize your items with categories. Add new ones or delete categories you don\'t use.')}</p>
        </div>

        {/* Modern Category Creator Card */}
        <div className="category-creator-card">
          <div className="category-creator-header">
            <div className="creator-icon">
              <Plus size={24} />
            </div>
            <div className="creator-info">
              <h4>{t('addNewCategory', 'Add New Category')}</h4>
              <p>{t('createCategoryHint', 'Create custom categories for your shopping lists')}</p>
            </div>
          </div>
          
          <div className="category-input-modern">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder={t('categoryNamePlaceholder', 'Enter category name...')}
              className="modern-category-input"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            />
            <button
              onClick={handleAddCategory}
              disabled={!newCategory.trim()}
              className="modern-add-btn"
              title={t('addCategory', 'Add Category')}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {/* Modern Categories Display */}
        <div className="categories-display-card">
          <div className="categories-header">
            <div className="categories-title">
              <List size={20} />
              <span>{t('yourCategories', 'Your Categories')} ({localCategories.length})</span>
            </div>
          </div>
          
          <div className="categories-content">
            {localCategories.length > 0 ? (
              <div className="modern-categories-grid">
                {localCategories.map((category, index) => {
                  const standardCategories = [
                    'Food',
                    'Household', 
                    'Personal Care',
                    'Work/Office',
                    'Shopping',
                    'Other'
                  ];
                  const categoryType = standardCategories.includes(category) ? 'standard' : 'custom';
                  
                  return (
                    <div key={`category-${category}-${index}`} className="modern-category-item">
                      <div className="category-content">
                        <div className="category-avatar">
                          {category.charAt(0).toUpperCase()}
                        </div>
                        <div className="category-details">
                          <span className="category-name">{category}</span>
                          <span className={`category-meta ${categoryType}`}>
                            {categoryType === 'standard' 
                              ? t('standardCategory', 'Standard') 
                              : t('customCategory', 'Custom')
                            }
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteCategory(category)}
                        className="modern-delete-btn"
                        title={t('deleteCategory', 'Delete Category')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="modern-empty-state">
                <div className="empty-illustration">
                  <div className="empty-circle">
                    <List size={32} />
                  </div>
                </div>
                <div className="empty-content">
                  <h4>{t('noCategories', 'No Categories Yet')}</h4>
                  <p>{t('emptyCategoriesHint', 'Start by creating your first category above. Categories help you organize your shopping items efficiently.')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Notification preferences state
  const [notificationSettings, setNotificationSettings] = useState(notificationService.loadSettings());

  // Export data state
  const [exportLoading, setExportLoading] = useState(false);

  // Delete account state
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  // Data Retention preference state
  const [retentionPreferenceSaving, setRetentionPreferenceSaving] = useState(false);

  // Privacy Settings state
  const [privacySettingsSaving, setPrivacySettingsSaving] = useState(false);
  const [privacySettings, setPrivacySettings] = useState(() => {
    return user?.privacySettings || {
      profileVisibility: false,
      dataSharing: false,
      analyticsOptIn: false,
      marketingEmails: false,
      activityLogging: false
    };
  });

  // Update privacy settings when user changes
  useEffect(() => {
    if (user?.privacySettings) {
      setPrivacySettings(user.privacySettings);
    }
  }, [user?.privacySettings]);

  // Data Retention preference handler
  const handleRetentionPreferenceChange = async (newPreference) => {
    if (retentionPreferenceSaving) return;
    
    try {
      setRetentionPreferenceSaving(true);
      
      // Use the AuthContext updateUser which handles API calls and state updates
      await updateUser({
        dataRetentionPreference: newPreference
      });
      
      // No success message - user can see the selection change immediately
    } catch (error) {
      console.error('Failed to update retention preference:', error);
      showAlert(
        t('retentionPreferenceUpdateFailed', 'Failed to update retention preference. Please try again.'),
        t('error', 'Error')
      );
    } finally {
      setRetentionPreferenceSaving(false);
    }
  };

  // Privacy Settings handler
  const handlePrivacySettingChange = async (settingKey, value) => {
    if (privacySettingsSaving) return;
    
    try {
      setPrivacySettingsSaving(true);
      
      const newSettings = { ...privacySettings, [settingKey]: value };
      setPrivacySettings(newSettings);
      
      // Update via API
      await apiService.updatePrivacySettings(newSettings);
      
      // Update user context
      await updateUser({ privacySettings: newSettings });
      
      // No success message - immediate visual feedback is enough
    } catch (error) {
      console.error('Failed to update privacy setting:', error);
      // Revert the setting on error
      setPrivacySettings(privacySettings);
      showAlert(
        t('privacySettingUpdateFailed', 'Failed to update privacy setting. Please try again.'),
        t('error', 'Error')
      );
    } finally {
      setPrivacySettingsSaving(false);
    }
  };

  // Load notification settings
  useEffect(() => {
    setNotificationSettings(notificationService.loadSettings());
  }, []);

  // Save notification settings
  const updateNotificationSetting = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    notificationService.updateSettings(newSettings);
    
    // Save to user profile if authenticated
    if (user) {
      try {
        await apiService.updateUser({ notificationSettings: newSettings });
        console.log('Notification settings saved to profile');
      } catch (error) {
        console.error('Failed to save notification settings:', error);
      }
    }

    // Request permission for push notifications if enabled
    if (key === 'pushNotifications' && value) {
      const permission = await notificationService.requestPermissions();
      if (permission !== 'granted') {
        // Revert setting if permission denied
        const revertedSettings = { ...newSettings, pushNotifications: false };
        setNotificationSettings(revertedSettings);
        notificationService.updateSettings(revertedSettings);
        showAlert(t('pushNotificationsDenied', 'Push notifications permission was denied. Please enable it in your browser settings.'), t('permissionDenied', 'Permission Denied'));
      }
    }
  };

  // Test notification function
  const testNotification = (type) => {
    try {
      const messages = {
        listShared: t('testListShared', 'John shared "Grocery List" with you'),
        listUpdated: t('testListUpdated', 'Mary updated "Weekend Plans" - 2 new items added'),
        itemAdded: t('testItemAdded', 'New item "Milk" was added to "Grocery List"'),
        itemCompleted: t('testItemCompleted', 'Sarah completed "Buy groceries" in "Weekend Plans"')
      };

      notificationService.testNotification(type, messages[type]);
    } catch (error) {
      showAlert(error.message, t('testNotification', 'Test Notification'));
    }
  };

  // Export user data function
  const handleExportData = async () => {
    setExportLoading(true);
    
    try {
      const result = await apiService.exportUserData();
      
      // Get the translated message and replace the filename placeholder
      const successMessage = t('exportSuccess', 'Your data has been exported successfully! File: {filename}')
        .replace('{filename}', result.filename);
      
      showAlert(
        successMessage,
        t('exportComplete', 'Export Complete')
      );
      
      console.log('✅ Export completed successfully:', result.filename);
    } catch (error) {
      console.error('❌ Export failed:', error);
      showAlert(
        t('exportError', `Failed to export data: ${error.message}`),
        t('exportFailed', 'Export Failed')
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Delete account function
  const handleDeleteAccount = async () => {
    // Show initial warning
    showConfirm(
      t('deleteAccountWarning', '⚠️ DANGER: Delete Account\n\nThis action will PERMANENTLY delete:\n\n• All your shopping lists and items\n• Your account profile and settings\n• All shared lists and collaborations\n• All personal data and preferences\n\n🚨 THIS CANNOT BE UNDONE!\n\nAre you absolutely sure you want to continue?'),
      () => {
        // Show final confirmation with typed confirmation
        const confirmationModal = {
          show: true,
          type: 'delete-account',
          title: t('finalDeleteConfirmation', 'Final Confirmation'),
          message: t('typeDeleteToConfirm', 'To confirm account deletion, please type "DELETE" in the field below:'),
          onConfirm: async (confirmationText) => {
            if (confirmationText?.toUpperCase() !== 'DELETE') {
              showAlert(
                t('deleteConfirmationMismatch', 'You must type "DELETE" exactly to confirm account deletion.'),
                t('confirmationRequired', 'Confirmation Required')
              );
              return;
            }
            
            await executeAccountDeletion();
          },
          onCancel: () => setModal({ show: false, type: 'alert', title: '', message: '', onConfirm: null, onCancel: null })
        };
        
        setModal(confirmationModal);
      },
      t('deleteAccount', 'Delete Account')
    );
  };

  const executeAccountDeletion = async () => {
    setDeleteAccountLoading(true);
    
    try {
      // Call API to delete account
      await apiService.deleteAccount();
      
      // Show success message
      const successModal = {
        show: true,
        type: 'alert',
        title: t('accountDeleted', 'Account Deleted'),
        message: t('accountDeletedSuccess', 'Your account has been successfully deleted. You will be redirected to the login page.'),
        onConfirm: () => {
          // Clear all local storage and redirect to login
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
        },
        onCancel: null
      };
      
      setModal(successModal);
      
      console.log('✅ Account deleted successfully');
    } catch (error) {
      console.error('❌ Account deletion failed:', error);
      showAlert(
        t('deleteAccountError', `Failed to delete account: ${error.message || 'Unknown error occurred'}`),
        t('deletionFailed', 'Deletion Failed')
      );
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const renderNotificationsSection = () => (
    <div className="settings-section modern-notifications-section">
      {/* Notification Settings Card */}
      <div className="notification-settings-card">
        <div className="notification-settings-header">
          <div className="notifications-icon">
            <Bell size={24} />
          </div>
          <div className="notifications-info">
            <h4>{t('notifications', 'Notifications')}</h4>
            <p>{t('notificationSettingsDescription', 'Configure how you want to be notified about list changes and updates.')}</p>
          </div>
        </div>

        <div className="notification-settings-content">
          {/* Notification Methods */}
          <div className="notification-methods-section">
            <h5>{t('notificationMethods', 'Notification Methods')}</h5>
            <div className="notification-methods-list">
              <div className="notification-method">
                <div className="method-info">
                  <span className="method-name">{t('inAppNotifications', 'In-App Notifications')}</span>
                  <span className="method-desc">{t('inAppNotificationsDesc', 'Show notifications while using the app')}</span>
                </div>
                <label className="modern-toggle">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.inAppNotifications}
                    onChange={(e) => updateNotificationSetting('inAppNotifications', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="notification-method">
                <div className="method-info">
                  <span className="method-name">{t('pushNotifications', 'Push Notifications')}</span>
                  <span className="method-desc">{t('pushNotificationsDesc', 'Browser notifications even when app is closed')}</span>
                </div>
                <label className="modern-toggle">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.pushNotifications}
                    onChange={(e) => updateNotificationSetting('pushNotifications', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="notification-method">
                <div className="method-info">
                  <span className="method-name">{t('emailNotifications', 'Email Notifications')}</span>
                  <span className="method-desc">{t('emailNotificationsDesc', 'Receive updates via email')}</span>
                </div>
                <label className="modern-toggle">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => updateNotificationSetting('emailNotifications', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Notification Types */}
          <div className="notification-types-section">
            <h5>{t('notificationTypes', 'What to Notify About')}</h5>
            <div className="notification-types-list">
              <div className="notification-type">
                <div className="type-info">
                  <span className="type-name">{t('listSharedNotifications', 'List Shared')}</span>
                  <span className="type-desc">{t('listSharedNotificationsDescription', 'When someone shares a list with you')}</span>
                </div>
                <div className="type-controls">
                  <label className="modern-toggle">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.listShared}
                      onChange={(e) => updateNotificationSetting('listShared', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button 
                    className="test-btn"
                    onClick={() => testNotification('listShared')}
                    title={t('testNotification', 'Test Notification')}
                  >
                    {t('test', 'Test')}
                  </button>
                </div>
              </div>

              <div className="notification-type">
                <div className="type-info">
                  <span className="type-name">{t('listUpdatedNotifications', 'List Updated')}</span>
                  <span className="type-desc">{t('listUpdatedNotificationsDescription', 'When shared lists are modified')}</span>
                </div>
                <div className="type-controls">
                  <label className="modern-toggle">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.listUpdated}
                      onChange={(e) => updateNotificationSetting('listUpdated', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button 
                    className="test-btn"
                    onClick={() => testNotification('listUpdated')}
                    title={t('testNotification', 'Test Notification')}
                  >
                    {t('test', 'Test')}
                  </button>
                </div>
              </div>

              <div className="notification-type">
                <div className="type-info">
                  <span className="type-name">{t('itemAddedNotifications', 'Items Added')}</span>
                  <span className="type-desc">{t('itemAddedNotificationsDescription', 'When new items are added to shared lists')}</span>
                </div>
                <div className="type-controls">
                  <label className="modern-toggle">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.itemAdded}
                      onChange={(e) => updateNotificationSetting('itemAdded', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button 
                    className="test-btn"
                    onClick={() => testNotification('itemAdded')}
                    title={t('testNotification', 'Test Notification')}
                  >
                    {t('test', 'Test')}
                  </button>
                </div>
              </div>

              <div className="notification-type">
                <div className="type-info">
                  <span className="type-name">{t('itemCompletedNotifications', 'Items Completed')}</span>
                  <span className="type-desc">{t('itemCompletedNotificationsDescription', 'When items are marked as completed')}</span>
                </div>
                <div className="type-controls">
                  <label className="modern-toggle">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.itemCompleted}
                      onChange={(e) => updateNotificationSetting('itemCompleted', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button 
                    className="test-btn"
                    onClick={() => testNotification('itemCompleted')}
                    title={t('testNotification', 'Test Notification')}
                  >
                    {t('test', 'Test')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Test All Button */}
          <div className="notification-test-section">
            <button 
              className="test-all-notifications-btn"
              onClick={() => {
                testNotification('listShared');
                setTimeout(() => testNotification('listUpdated'), 1000);
              }}
            >
              {t('testAllNotifications', 'Test All Notifications')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDataSection = () => (
    <div className="modern-data-privacy-section">
      <div className="data-privacy-card">
        <div className="data-privacy-header">
          <div className="data-icon">
            <Database size={24} />
          </div>
          <div className="data-info">
            <h4>{t('dataAndPrivacy', 'Data & Privacy')}</h4>
            <p>{t('dataAndPrivacyDescription', 'Manage your data and privacy settings.')}</p>
          </div>
        </div>
        
        <div className="data-privacy-content">
          <div className="data-section">
            <h5>{t('dataManagement', 'Data Management')}</h5>
            
            <div className="data-option">
              <div className="option-info">
                <div className="option-name">{t('exportData', 'Export Data')}</div>
                <div className="option-desc">{t('exportDataDescription', 'Download a copy of your lists and data.')}</div>
              </div>
              <button 
                className="data-action-btn export-btn" 
                onClick={handleExportData}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    <div className="spinner"></div>
                    {t('exporting', 'Exporting...')}
                  </>
                ) : (
                  t('exportMyData', 'Export My Data')
                )}
              </button>
            </div>
            
            {/* Data Retention Preference Section - Standalone */}
            <div className="retention-preference-standalone">
              <div className="retention-preference-header">
                <h6 className="retention-title">{t('dataRetentionPreference', 'Data Retention Preference')}</h6>
                <p className="retention-description">{t('dataRetentionPreferenceDescription', 'Choose how long to keep your account and data if you become inactive.')}</p>
              </div>
              
              <div className="retention-preference-options">
                <div className={`retention-option ${(user?.dataRetentionPreference || 'two_years') === 'one_year' ? 'selected' : ''}`}>
                  <label className="retention-option-label">
                    <input
                      type="radio"
                      name="dataRetentionPreference"
                      value="one_year"
                      checked={(user?.dataRetentionPreference || 'two_years') === 'one_year'}
                      onChange={() => handleRetentionPreferenceChange('one_year')}
                      disabled={retentionPreferenceSaving}
                    />
                    <div className="retention-option-content">
                      <div className="retention-option-header">
                        <span className="retention-option-title">{t('oneYearRetention', 'Delete my data after 1 year of inactivity')}</span>
                        <span className="retention-option-badge one-year">1Y</span>
                      </div>
                      <div className="retention-option-description">
                        {t('oneYearDescription', 'Your account and all lists will be permanently deleted if you don\'t log in for 1 year.')}
                      </div>
                    </div>
                  </label>
                </div>

                <div className={`retention-option ${(user?.dataRetentionPreference || 'two_years') === 'two_years' ? 'selected' : ''}`}>
                  <label className="retention-option-label">
                    <input
                      type="radio"
                      name="dataRetentionPreference"
                      value="two_years"
                      checked={(user?.dataRetentionPreference || 'two_years') === 'two_years'}
                      onChange={() => handleRetentionPreferenceChange('two_years')}
                      disabled={retentionPreferenceSaving}
                    />
                    <div className="retention-option-content">
                      <div className="retention-option-header">
                        <span className="retention-option-title">{t('twoYearsRetention', 'Delete my data after 2 years of inactivity')}</span>
                        <span className="retention-option-badge two-years">2Y</span>
                      </div>
                      <div className="retention-option-description">
                        {t('twoYearsDescription', 'Your account and all lists will be permanently deleted if you don\'t log in for 2 years.')}
                      </div>
                    </div>
                  </label>
                </div>

                <div className={`retention-option ${(user?.dataRetentionPreference || 'two_years') === 'indefinite' ? 'selected' : ''}`}>
                  <label className="retention-option-label">
                    <input
                      type="radio"
                      name="dataRetentionPreference"
                      value="indefinite"
                      checked={(user?.dataRetentionPreference || 'two_years') === 'indefinite'}
                      onChange={() => handleRetentionPreferenceChange('indefinite')}
                      disabled={retentionPreferenceSaving}
                    />
                    <div className="retention-option-content">
                      <div className="retention-option-header">
                        <span className="retention-option-title">{t('indefiniteRetention', 'Keep my data indefinitely (never auto-delete)')}</span>
                        <span className="retention-option-badge indefinite">∞</span>
                      </div>
                      <div className="retention-option-description">
                        {t('indefiniteDescription', 'Your data will never be automatically deleted due to inactivity. Only manual deletion is possible.')}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {retentionPreferenceSaving && (
                <div className="retention-saving-indicator">
                  <div className="spinner small"></div>
                  <span>{t('saving', 'Saving...')}</span>
                </div>
              )}

              <div className="retention-info-panel">
                <div className="retention-info-item">
                  <span className="retention-info-label">{t('lastLoginLabel', 'Last Login')}:</span>
                  <span className="retention-info-value">
                    {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : t('unknown', 'Unknown')}
                  </span>
                </div>
                <div className="retention-info-item">
                  <span className="retention-info-label">{t('currentRetentionLabel', 'Current Setting')}:</span>
                  <span className="retention-info-value">
                    {(user?.dataRetentionPreference || 'two_years') === 'one_year' && t('oneYear', '1 Year')}
                    {(user?.dataRetentionPreference || 'two_years') === 'two_years' && t('twoYears', '2 Years')}
                    {(user?.dataRetentionPreference || 'two_years') === 'indefinite' && t('indefinite', 'Indefinite')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="privacy-section">
            <div className="privacy-header">
              <h5>{t('privacySettings', 'Privacy Settings')}</h5>
              <div className="privacy-promise">
                <div className="privacy-shield">🛡️</div>
                <div className="privacy-message">
                  <p className="privacy-guarantee">
                    <strong>{t('privacyGuarantee', 'Your Privacy is Our Priority')}</strong>
                  </p>
                  <p className="privacy-description">
                    {t('privacyDescription', 'ListWiz is a safe space that respects your privacy. All settings below start OFF and nothing is shared without your explicit consent. You have complete control over your data.')}
                  </p>
                </div>
              </div>
            </div>

            {privacySettingsSaving && (
              <div className="privacy-saving-indicator">
                <div className="spinner small"></div>
                <span>{t('updatingPrivacySettings', 'Updating privacy settings...')}</span>
              </div>
            )}
            
            <div className="privacy-option">
              <div className="option-info">
                <div className="option-name">
                  {t('profileVisibility', 'Profile Visibility')}
                  <span className="privacy-badge off">{privacySettings.profileVisibility ? t('on', 'ON') : t('off', 'OFF')}</span>
                </div>
                <div className="option-desc">
                  {t('profileVisibilityDescription', 'Allow other users to see your name when you share lists. When OFF, you appear as "Anonymous User" in shared lists.')}
                </div>
              </div>
              <div className="modern-toggle">
                <input 
                  type="checkbox" 
                  id="profileVisibility" 
                  checked={privacySettings.profileVisibility}
                  onChange={(e) => handlePrivacySettingChange('profileVisibility', e.target.checked)}
                  disabled={privacySettingsSaving}
                />
                <label htmlFor="profileVisibility" className="toggle-slider"></label>
              </div>
            </div>

            <div className="privacy-option">
              <div className="option-info">
                <div className="option-name">
                  {t('dataSharing', 'Anonymous Usage Data')}
                  <span className="privacy-badge off">{privacySettings.dataSharing ? t('on', 'ON') : t('off', 'OFF')}</span>
                </div>
                <div className="option-desc">
                  {t('dataSharingDescription', 'Help improve ListWiz by sharing completely anonymous usage statistics. No personal data, list contents, or identifying information is ever shared.')}
                </div>
              </div>
              <div className="modern-toggle">
                <input 
                  type="checkbox" 
                  id="dataSharing" 
                  checked={privacySettings.dataSharing}
                  onChange={(e) => handlePrivacySettingChange('dataSharing', e.target.checked)}
                  disabled={privacySettingsSaving}
                />
                <label htmlFor="dataSharing" className="toggle-slider"></label>
              </div>
            </div>

            <div className="privacy-option">
              <div className="option-info">
                <div className="option-name">
                  {t('analyticsOptIn', 'Analytics & Insights')}
                  <span className="privacy-badge off">{privacySettings.analyticsOptIn ? t('on', 'ON') : t('off', 'OFF')}</span>
                </div>
                <div className="option-desc">
                  {t('analyticsOptInDescription', 'Receive anonymized insights about your list usage patterns. This data never leaves your device and helps you understand your shopping habits.')}
                </div>
              </div>
              <div className="modern-toggle">
                <input 
                  type="checkbox" 
                  id="analyticsOptIn" 
                  checked={privacySettings.analyticsOptIn}
                  onChange={(e) => handlePrivacySettingChange('analyticsOptIn', e.target.checked)}
                  disabled={privacySettingsSaving}
                />
                <label htmlFor="analyticsOptIn" className="toggle-slider"></label>
              </div>
            </div>

            <div className="privacy-option">
              <div className="option-info">
                <div className="option-name">
                  {t('marketingEmails', 'Feature Updates')}
                  <span className="privacy-badge off">{privacySettings.marketingEmails ? t('on', 'ON') : t('off', 'OFF')}</span>
                </div>
                <div className="option-desc">
                  {t('marketingEmailsDescription', 'Receive occasional emails about new features and improvements. No spam, no third-party emails, and you can unsubscribe anytime.')}
                </div>
              </div>
              <div className="modern-toggle">
                <input 
                  type="checkbox" 
                  id="marketingEmails" 
                  checked={privacySettings.marketingEmails}
                  onChange={(e) => handlePrivacySettingChange('marketingEmails', e.target.checked)}
                  disabled={privacySettingsSaving}
                />
                <label htmlFor="marketingEmails" className="toggle-slider"></label>
              </div>
            </div>

            <div className="privacy-option">
              <div className="option-info">
                <div className="option-name">
                  {t('activityLogging', 'Activity Logging')}
                  <span className="privacy-badge off">{privacySettings.activityLogging ? t('on', 'ON') : t('off', 'OFF')}</span>
                </div>
                <div className="option-desc">
                  {t('activityLoggingDescription', 'Keep a private log of your list activities for troubleshooting and data recovery. Logs are encrypted and only accessible by you.')}
                </div>
              </div>
              <div className="modern-toggle">
                <input 
                  type="checkbox" 
                  id="activityLogging" 
                  checked={privacySettings.activityLogging}
                  onChange={(e) => handlePrivacySettingChange('activityLogging', e.target.checked)}
                  disabled={privacySettingsSaving}
                />
                <label htmlFor="activityLogging" className="toggle-slider"></label>
              </div>
            </div>

            <div className="privacy-footer">
              <div className="privacy-commitment">
                <h6>{t('ourCommitment', 'Our Privacy Commitment')}</h6>
                <ul className="privacy-promises">
                  <li>{t('noSelling', '🚫 We never sell your data')}</li>
                  <li>{t('noTracking', '🔒 No cross-site tracking')}</li>
                  <li>{t('localFirst', '💾 Your data stays on your device when possible')}</li>
                  <li>{t('encryptedStorage', '🔐 All data is encrypted')}</li>
                  <li>{t('rightToDelete', '🗑️ Full right to delete your data anytime')}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="danger-zone">
            <h5>{t('dangerZone', 'Danger Zone')}</h5>
            
            <div className="danger-option">
              <div className="option-info">
                <div className="option-name">{t('deleteAccount', 'Delete Account')}</div>
                <div className="option-desc">{t('deleteAccountDescription', 'Permanently delete your account and all associated data.')}</div>
              </div>
              <button 
                className="data-action-btn delete-btn btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleteAccountLoading}
                style={{
                  background: deleteAccountLoading ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: '2px solid #dc2626',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {deleteAccountLoading ? (
                  <>
                    <div className="spinner"></div>
                    {t('deleting', 'Deleting...')}
                  </>
                ) : (
                  t('deleteMyAccount', 'Delete My Account')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdminSection = () => (
    <div className="modern-admin-section">
      {/* Header Section */}
      <div className="admin-header-banner">
        <div className="admin-hero-content">
          <div className="admin-icon-container">
            <div className="admin-icon-wrapper">
              <Shield size={32} />
            </div>
          </div>
          <div className="admin-title-section">
            <h2>{t('administration', 'Administration')}</h2>
            <p>{t('adminDescription', 'Comprehensive system management and oversight tools.')}</p>
          </div>
        </div>
      </div>

      {/* Admin Dashboard Cards */}
      <div className="admin-dashboard-grid">
        
        {/* User Management Card */}
        <div className="admin-card user-management-card">
          <div className="admin-card-header">
            <div className="admin-card-icon">
              <Users size={24} />
            </div>
            <div className="admin-card-info">
              <h3>{t('userManagement', 'User Management')}</h3>
              <p>{t('userManagementDescription', 'Monitor and manage all system users.')}</p>
            </div>
            <button 
              className="admin-toggle-btn"
              onClick={() => {
                setShowUserManagement(!showUserManagement);
                if (!showUserManagement) {
                  loadAdminData();
                }
              }}
            >
              {showUserManagement ? (
                <>
                  <ChevronUp size={16} />
                  {t('hideUsers', 'Hide Users')}
                </>
              ) : (
                <>
                  <Users size={16} />
                  {t('manageUsers', 'Manage Users')}
                </>
              )}
            </button>
          </div>

          {showUserManagement && (
            <div className="admin-card-content">
              {adminError && (
                <div className="admin-error-banner">
                  <AlertTriangle size={20} />
                  <span>{adminError}</span>
                  <button onClick={() => setAdminError(null)}>×</button>
                </div>
              )}

              {usersLoading ? (
                <div className="admin-loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingAdminData', 'Loading admin data...')}</p>
                </div>
              ) : (
                <div className="users-management-container">
                  <div className="users-stats-row">
                    <div className="user-stat">
                      <span className="stat-number">{allUsers.length}</span>
                      <span className="stat-label">{t('totalUsers', 'Total Users')}</span>
                    </div>
                    <div className="user-stat">
                      <span className="stat-number">{allUsers.filter(u => u.isAdmin).length}</span>
                      <span className="stat-label">{t('administrators', 'Administrators')}</span>
                    </div>
                    <div className="user-stat">
                      <span className="stat-number">{allUsers.filter(u => u.isBlocked).length}</span>
                      <span className="stat-label">{t('blockedUsers', 'Blocked Users')}</span>
                    </div>
                  </div>

                  {/* Users List Container */}
                  <div className="lists-container">
                    <div className="lists-header">
                      <h3>{t('users', 'Users')} ({allUsers.length})</h3>
                      <div className="lists-controls">
                        <div className="lists-search">
                          <Search className="search-icon" size={14} />
                          <input 
                            type="text" 
                            placeholder={t('searchUsers', 'Search users...')}
                            value=""
                            onChange={() => {}}
                          />
                        </div>
                        <div className="lists-filter">
                          <select>
                            <option value="all">{t('allUsers', 'All Users')}</option>
                            <option value="admin">{t('administrators', 'Administrators')}</option>
                            <option value="blocked">{t('blockedUsers', 'Blocked')}</option>
                            <option value="active">{t('activeUsers', 'Active')}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="lists-list">
                      {allUsers.slice(0, 10).map((userData) => (
                        <div key={userData.id} className="list-item">
                          <div className="list-avatar">
                            {userData.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="list-info">
                            <div className="list-info-main">
                              <h4 className="list-title">{userData.name}</h4>
                              <div className="list-meta">
                                <span className="list-owner">{userData.email}</span>
                                <span className="list-items-count">
                                  <List size={12} />
                                  {userData.stats?.totalLists || 0} {t('lists', 'lists')}
                                </span>
                                <span className="list-completion">
                                  <Share2 size={12} />
                                  {userData.stats?.sharedLists || 0} {t('shared', 'shared')}
                                </span>
                                <div className="list-sharing-status">
                                  {userData.isAdmin && (
                                    <span className="sharing-badge admin">
                                      <Shield size={10} />
                                      {t('admin', 'Admin')}
                                    </span>
                                  )}
                                  {userData.isBlocked && (
                                    <span className="sharing-badge blocked">
                                      <Ban size={10} />
                                      {t('blocked', 'Blocked')}
                                    </span>
                                  )}
                                  {!userData.isAdmin && !userData.isBlocked && (
                                    <span className="sharing-badge active">
                                      <User size={10} />
                                      {t('active', 'Active')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="list-actions">
                            <button
                              onClick={() => handleToggleAdmin(userData.id, userData.isAdmin)}
                              className={`list-action ${userData.isAdmin ? 'admin-remove' : 'admin-add'}`}
                              title={userData.isAdmin ? t('removeAdmin', 'Remove admin') : t('makeAdmin', 'Make admin')}
                            >
                              {userData.isAdmin ? <ShieldOff size={14} /> : <Shield size={14} />}
                            </button>
                            <button
                              onClick={() => handleToggleBlocked(userData.id, userData.isBlocked)}
                              className={`list-action ${userData.isBlocked ? 'unblock' : 'block'}`}
                              title={userData.isBlocked ? t('unblockUser', 'Unblock user') : t('blockUser', 'Block user')}
                            >
                              {userData.isBlocked ? <UserCheck size={14} /> : <Ban size={14} />}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userData.id)}
                              className="list-action delete"
                              title={t('deleteUser', 'Delete user')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    <div className="lists-pagination">
                      <div className="pagination-info">
                        {t('showingUsers', 'Showing {0}-{1} of {2} users', [1, Math.min(10, allUsers.length), allUsers.length])}
                      </div>
                      <div className="pagination-controls">
                        <button className="pagination-btn" disabled>
                          <ChevronLeft size={14} />
                          {t('previous', 'Previous')}
                        </button>
                        <button className="pagination-btn active">1</button>
                        {allUsers.length > 10 && (
                          <button className="pagination-btn">2</button>
                        )}
                        <button className="pagination-btn" disabled={allUsers.length <= 10}>
                          {t('next', 'Next')}
                          <ChevronRight size={14} />
                        </button>
                      </div>
                      <div className="page-size-selector">
                        <span>{t('show', 'Show')}:</span>
                        <select defaultValue="10">
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* System Statistics Card */}
        <div className="admin-card system-stats-card">
          <div className="admin-card-header">
            <div className="admin-card-icon">
              <TrendingUp size={24} />
            </div>
            <div className="admin-card-info">
              <h3>{t('systemStats', 'System Statistics')}</h3>
              <p>{t('systemStatsDescription', 'Real-time system metrics and analytics.')}</p>
            </div>
            <button 
              className="admin-toggle-btn"
              onClick={() => {
                setShowSystemStats(!showSystemStats);
                if (!showSystemStats) {
                  loadAdminData();
                }
              }}
            >
              {showSystemStats ? (
                <>
                  <ChevronUp size={16} />
                  {t('hideStats', 'Hide Stats')}
                </>
              ) : (
                <>
                  <BarChart3 size={16} />
                  {t('viewStats', 'View Stats')}
                </>
              )}
            </button>
          </div>

          {showSystemStats && (
            <div className="admin-card-content">
              {adminError && (
                <div className="admin-error-banner">
                  <AlertTriangle size={20} />
                  <span>{adminError}</span>
                  <button onClick={() => setAdminError(null)}>×</button>
                </div>
              )}

              {usersLoading ? (
                <div className="admin-loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingAdminData', 'Loading system data...')}</p>
                </div>
              ) : adminStats && (
                <div className="system-stats-container">
                  <div className="stats-grid-modern">
                    <div className="stat-card-modern">
                      <div className="stat-icon-wrapper users">
                        <Users size={20} />
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{adminStats.overview?.totalUsers || allUsers.length}</span>
                        <span className="stat-label">{t('totalUsers', 'Total Users')}</span>
                        <span className="stat-detail">{adminStats.overview?.usersWithLists || 0} {t('withLists', 'with lists')}</span>
                      </div>
                    </div>

                    <div className="stat-card-modern">
                      <div className="stat-icon-wrapper lists">
                        <List size={20} />
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{adminStats.overview?.totalLists || adminLists.length}</span>
                        <span className="stat-label">{t('totalLists', 'Total Lists')}</span>
                        <span className="stat-detail">{adminStats.overview?.averageListsPerUser || '0'} {t('avgPerUser', 'avg per user')}</span>
                      </div>
                    </div>

                    <div className="stat-card-modern">
                      <div className="stat-icon-wrapper items">
                        <Package size={20} />
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{adminStats.overview?.totalItems || 0}</span>
                        <span className="stat-label">{t('totalItems', 'Total Items')}</span>
                        <span className="stat-detail">{adminStats.overview?.completedItems || 0} {t('completed', 'completed')}</span>
                      </div>
                    </div>

                    <div className="stat-card-modern">
                      <div className="stat-icon-wrapper completion">
                        <TrendingUp size={20} />
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">
                          {adminStats.overview?.completionRate || 
                            (adminStats.overview?.totalItems > 0 ? 
                              Math.round((adminStats.overview?.completedItems / adminStats.overview?.totalItems) * 100) : 0)}%
                        </span>
                        <span className="stat-label">{t('completionRate', 'Completion Rate')}</span>
                        <span className="stat-detail">{t('systemAverage', 'System average')}</span>
                      </div>
                    </div>
                  </div>

                  {adminStats.systemHealth && (
                    <div className="system-health-card">
                      <h4>{t('systemHealth', 'System Health')}</h4>
                      <div className="health-metrics">
                        <div className="health-metric">
                          <Clock size={16} />
                          <span>{t('uptime', 'Uptime')}: {formatUptime(adminStats.systemHealth.uptime || 0)}</span>
                        </div>
                        <div className="health-metric">
                          <Database size={16} />
                          <span>
                            {t('memory', 'Memory')}: {formatMemory(adminStats.systemHealth.memoryUsage?.heapUsed || 0)} / 
                            {formatMemory(adminStats.systemHealth.memoryUsage?.heapTotal || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* List Management Card */}
        <div className="admin-card list-management-card">
          <div className="admin-card-header">
            <div className="admin-card-icon">
              <List size={24} />
            </div>
            <div className="admin-card-info">
              <h3>{t('listManagement', 'List Management')}</h3>
              <p>{t('listManagementDescription', 'Monitor all lists and their usage patterns.')}</p>
            </div>
            <button 
              className="admin-toggle-btn"
              onClick={() => {
                setShowListManagement(!showListManagement);
                if (!showListManagement) {
                  loadAdminData();
                }
              }}
            >
              {showListManagement ? (
                <>
                  <ChevronUp size={16} />
                  {t('hideLists', 'Hide Lists')}
                </>
              ) : (
                <>
                  <List size={16} />
                  {t('viewLists', 'View Lists')}
                </>
              )}
            </button>
          </div>

          {showListManagement && (
            <div className="admin-card-content">
              {adminError && (
                <div className="admin-error-banner">
                  <AlertTriangle size={20} />
                  <span>{adminError}</span>
                  <button onClick={() => setAdminError(null)}>×</button>
                </div>
              )}

              {usersLoading ? (
                <div className="admin-loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingAdminData', 'Loading lists data...')}</p>
                </div>
              ) : (
                <div className="lists-management-container">
                  <div className="admin-list-controls">
                    <div className="list-search-filter">
                      <Search size={16} />
                      <input 
                        type="text" 
                        placeholder={t('searchLists', 'Search lists...')} 
                        className="list-search-input"
                      />
                    </div>
                    <div className="list-stats-compact">
                      <span className="stat-compact">
                        <strong>{adminLists.length}</strong> {t('total', 'Total')}
                      </span>
                      <span className="stat-compact">
                        <strong>{adminLists.filter(l => l.sharedWith && l.sharedWith.length > 0).length}</strong> {t('shared', 'Shared')}
                      </span>
                    </div>
                  </div>

                  <div className="compact-list-container">
                    {adminLists.map((list, index) => (
                      <div key={list.id} className={`compact-list-item ${list.isBlocked ? 'blocked' : ''}`}>
                        <div className="list-id">{list.id}</div>
                        <div className="list-main">
                          <div className="list-name-owner">
                            <span className="list-name">
                              {list.name}
                              {list.isBlocked && (
                                <span className="blocked-indicator" title={t('thisListIsBlocked', 'This list is blocked')}>
                                  <Ban size={12} />
                                </span>
                              )}
                            </span>
                            <span className="list-owner">{t('by', 'by')} {list.owner?.name}</span>
                          </div>
                        </div>
                        <div className="list-items">{list.stats?.totalItems || 0} {t('items', 'items')}</div>
                        <div className="list-progress">{list.stats?.progress || 0}%</div>
                        <div className="list-sharing-status">
                          {list.sharedWith && list.sharedWith.length > 0 ? (
                            <span className="sharing-badge shared">
                              <Share2 size={12} />
                              {list.sharedWith.length}
                            </span>
                          ) : (
                            <span className="sharing-badge private">
                              <Lock size={12} />
                              {t('private', 'Private')}
                            </span>
                          )}
                        </div>
                        <div className="list-date">{formatDate(list.createdAt)}</div>
                        <div className="list-actions">
                          <button 
                            className="list-action view"
                            title={t('viewListDetails', 'View List Details')}
                            onClick={() => handleViewList(list.id)}
                          >
                            <Eye size={14} />
                          </button>
                          <button 
                            className="list-action edit"
                            title={t('editList', 'Edit List')}
                            onClick={() => handleEditList(list.id)}
                          >
                            <Edit2 size={14} />
                          </button>
                          {list.isBlocked ? (
                            <button 
                              className="list-action unblock"
                              title={t('unblockList', 'Unblock List')}
                              onClick={() => handleToggleListBlock(list.id, false)}
                            >
                              <Play size={14} />
                            </button>
                          ) : (
                            <button 
                              className="list-action block"
                              title={t('blockList', 'Block List')}
                              onClick={() => handleToggleListBlock(list.id, true)}
                            >
                              <Ban size={14} />
                            </button>
                          )}
                          <button 
                            className="list-action transfer"
                            title={t('transferOwnership', 'Transfer Ownership')}
                            onClick={() => handleTransferOwnership(list.id)}
                          >
                            <UserCheck size={14} />
                          </button>
                          <button 
                            className="list-action delete"
                            title={t('deleteList', 'Delete List')}
                            onClick={() => handleDeleteList(list.id, list.name)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="admin-pagination">
                    <div className="pagination-info">
                      {t('showingLists', 'Showing {0}-{1} of {2} lists', [1, Math.min(10, adminLists.length), adminLists.length])}
                    </div>
                    <div className="pagination-controls">
                      <button className="pagination-btn" disabled>
                        <ChevronDown size={16} style={{transform: 'rotate(90deg)'}} />
                      </button>
                      <span className="pagination-current">1</span>
                      <button className="pagination-btn" disabled>
                        <ChevronDown size={16} style={{transform: 'rotate(-90deg)'}} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Data Retention Card */}
        <div className="admin-card data-retention-card">
          <div className="admin-card-header">
            <div className="admin-card-icon">
              <Database size={24} />
            </div>
            <div className="admin-card-info">
              <h3>{t('dataRetention', 'Data Retention')}</h3>
              <p>{t('dataRetentionDescription', 'Automated data cleanup and retention policies.')}</p>
            </div>
            <button 
              className="admin-toggle-btn"
              onClick={() => {
                setShowDataRetention(!showDataRetention);
                if (!showDataRetention) {
                  loadDataRetentionData();
                }
              }}
            >
              {showDataRetention ? (
                <>
                  <ChevronUp size={16} />
                  {t('hideDataRetention', 'Hide Data Retention')}
                </>
              ) : (
                <>
                  <Database size={16} />
                  {t('manageDataRetention', 'Manage Data Retention')}
                </>
              )}
            </button>
          </div>

          {showDataRetention && (
            <div className="admin-card-content">
              {retentionLoading ? (
                <div className="admin-loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingDataRetention', 'Loading data retention information...')}</p>
                </div>
              ) : (
                <div className="data-retention-container">
                  <div className="retention-overview">
                    <div className="retention-info">
                      <h4>{t('dataRetentionPolicies', 'Data Retention Policies')}</h4>
                      <p>{t('monitorDataCleanup', 'Monitor and manage automatic data cleanup to maintain system performance.')}</p>
                    </div>
                  </div>
                  
                  {retentionStats && (
                    <div className="retention-stats-grid">
                      <div className="retention-stat">
                        <span className="stat-number">{retentionStats.eligibleForCleanup || 0}</span>
                        <span className="stat-label">{t('itemsEligibleForCleanup', 'Items eligible for cleanup')}</span>
                      </div>
                      <div className="retention-stat">
                        <span className="stat-number">{formatDays(retentionConfig?.maxAge || 365)}</span>
                        <span className="stat-label">{t('maximumDataAge', 'Maximum data age')}</span>
                      </div>
                    </div>
                  )}

                  <div className="retention-actions">
                    <div className="cleanup-info">
                      <p>{t('dataCleanupInfo', 'Click the button below to manually run data cleanup. This will remove old shopping lists, completed items, and inactive data according to your retention policies.')}</p>
                      <p className="cleanup-warning">{t('dataCleanupWarning', '⚠️ This action permanently deletes data and cannot be undone.')}</p>
                    </div>
                    <button 
                      className="retention-action-btn"
                      onClick={handleDataRetentionCleanup}
                      disabled={cleanupLoading}
                    >
                      {cleanupLoading ? (
                        <>
                          <div className="mini-spinner"></div>
                          {t('runningCleanup', 'Running Cleanup...')}
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          {t('runDataCleanup', 'Run Data Cleanup')}
                        </>
                      )}
                    </button>
                  </div>

                  {cleanupResults && (
                    <div className="cleanup-results">
                      <h4>{t('lastCleanupResults', 'Last Cleanup Results')}</h4>
                      <div className="results-grid">
                        <div className="result-item">
                          <span className="result-label">{t('itemsCleaned', 'Items cleaned:')}</span>
                          <span className="result-value">{cleanupResults.itemsCleaned || 0}</span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">{t('listsCleaned', 'Lists cleaned:')}</span>
                          <span className="result-value">{cleanupResults.listsCleaned || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );

  const renderActiveSection = () => {
    console.log('renderActiveSection called with:', activeSection);
    console.log('Available sections:', settingsSections.map(s => s.id));
    console.log('User isAdmin in renderActiveSection:', user?.isAdmin);
    
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'languages':
        return renderLanguageSection();
      case 'categories':
        return renderCategoriesSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'data':
        return renderDataSection();
      case 'admin':
        console.log('Rendering admin section');
        return renderAdminSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="settings-page">
      <header className="app-header">
        <div className="header-content">
        <div className="header-branding">
          <div 
            className="header-title clickable-header" 
            onClick={() => {
              // Clean up localStorage when leaving settings  
              localStorage.removeItem('listWizSettingsSection');
              onBack();
            }}
            style={{ cursor: 'pointer' }}
            title="Back to Lists"
          >
            <img src="/favicon-96x96.png" alt="ListWiz Logo" className="header-icon" style={{width: '48px', height: '48px'}} />
            <div className="title-text">
              <h1>
                ListWiz
                <span className="version-number" style={{ 
                  fontSize: '10px', 
                  fontWeight: 'normal', 
                  color: '#666', 
                  marginLeft: '8px',
                  opacity: 0.7
                }}>
                  v0.1.0
                </span>
              </h1>
              <p className="app-slogan">{t('appSlogan', 'Smart Lists Made Simple')}</p>
            </div>
          </div>
        </div>          <div className="header-actions">
            <button onClick={() => {
              // Clean up localStorage when leaving settings
              localStorage.removeItem('listWizSettingsSection');
              onBack();
            }} className="back-btn">
              <ArrowLeft size={20} />
              <span>{t('settings', 'Settings')}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="settings-layout">
        <aside className="settings-sidebar">
          <nav className="settings-nav">
            {settingsSections.map((section) => {
              console.log('Rendering nav section:', section.id, section.name);
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`settings-nav-item ${activeSection === section.id ? 'active' : ''}`}
                >
                  <IconComponent size={20} />
                  <span>{section.name}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="settings-content">
          {renderActiveSection()}
        </main>
      </div>

      {/* View List Modal */}
      {viewListModal.show && (
        <div className="modal-overlay">
          <div className="modern-modal view-list-modal">
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <Eye size={24} />
                </div>
                <div className="modal-title-content">
                  <h3>{t('viewListDetails', 'List Details')}</h3>
                  <p className="modal-subtitle">{viewListModal.list?.name || t('loading', 'Loading...')}</p>
                </div>
              </div>
              <button onClick={closeViewListModal} className="modal-close-btn">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="modal-content">
              {viewListLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingListDetails', 'Loading list details...')}</p>
                </div>
              ) : viewListDetails ? (
                <div className="list-details-content">
                  {/* List Overview Card */}
                  <div className="detail-card overview-card">
                    <div className="detail-card-header">
                      <div className="detail-card-icon">
                        <Package size={20} />
                      </div>
                      <h4>{t('overview', 'Overview')}</h4>
                    </div>
                    <div className="detail-card-content">
                      <div className="overview-grid">
                        <div className="overview-item">
                          <div className="overview-icon">
                            <User size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('owner', 'Owner')}</span>
                            <span className="overview-value">{viewListDetails.ownerName}</span>
                          </div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-icon">
                            <Calendar size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('created', 'Created')}</span>
                            <span className="overview-value">{new Date(viewListDetails.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-icon">
                            <List size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('totalItems', 'Total Items')}</span>
                            <span className="overview-value">{viewListDetails.totalItems || 0}</span>
                          </div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-icon">
                            <CheckCircle size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('completed', 'Completed')}</span>
                            <span className="overview-value">{viewListDetails.completedItems || 0}</span>
                          </div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-icon">
                            <Users size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('shared', 'Shared')}</span>
                            <span className="overview-value">{viewListDetails.sharedUsers?.length || 0} {t('users', 'users')}</span>
                          </div>
                        </div>
                        <div className="overview-item">
                          <div className="overview-icon">
                            <BarChart3 size={16} />
                          </div>
                          <div className="overview-info">
                            <span className="overview-label">{t('progress', 'Progress')}</span>
                            <span className="overview-value">
                              {viewListDetails.totalItems > 0 
                                ? Math.round((viewListDetails.completedItems / viewListDetails.totalItems) * 100)
                                : 0
                              }%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* List Items Card */}
                  {viewListDetails.items && viewListDetails.items.length > 0 && (
                    <div className="detail-card items-card">
                      <div className="detail-card-header">
                        <div className="detail-card-icon">
                          <Package size={20} />
                        </div>
                        <h4>{t('items', 'Items')} ({viewListDetails.items.length})</h4>
                      </div>
                      <div className="detail-card-content">
                        <div className="items-grid">
                          {viewListDetails.items.map((item, index) => (
                            <div key={index} className={`item-card ${item.completed ? 'completed' : 'pending'}`}>
                              <div className="item-status">
                                {item.completed ? (
                                  <CheckCircle size={18} className="status-icon completed" />
                                ) : (
                                  <Clock size={18} className="status-icon pending" />
                                )}
                              </div>
                              <div className="item-details">
                                <div className="item-name">{item.text}</div>
                                <div className="item-meta">
                                  <span className="item-quantity">
                                    <Package size={12} />
                                    {item.quantity}
                                  </span>
                                  <span className="item-category">
                                    <List size={12} />
                                    {item.category}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Empty state for items */}
                  {(!viewListDetails.items || viewListDetails.items.length === 0) && (
                    <div className="detail-card empty-items-card">
                      <div className="detail-card-header">
                        <div className="detail-card-icon">
                          <Package size={20} />
                        </div>
                        <h4>{t('items', 'Items')}</h4>
                      </div>
                      <div className="detail-card-content">
                        <div className="empty-state">
                          <div className="empty-icon">
                            <Package size={48} />
                          </div>
                          <h4>{t('noItemsYet', 'No Items Yet')}</h4>
                          <p>{t('thisListNoItems', 'This list doesn\'t have any items.')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shared Users Card */}
                  {viewListDetails.sharedUsers && viewListDetails.sharedUsers.length > 0 && (
                    <div className="detail-card shared-users-card">
                      <div className="detail-card-header">
                        <div className="detail-card-icon">
                          <Users size={20} />
                        </div>
                        <h4>{t('sharedAccess', 'Shared Access')} ({viewListDetails.sharedUsers.length})</h4>
                      </div>
                      <div className="detail-card-content">
                        <div className="shared-users-grid">
                          {viewListDetails.sharedUsers.map((sharedUser, index) => (
                            <div key={index} className="shared-user-card">
                              <div className="user-avatar">
                                {sharedUser.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="user-info">
                                <div className="user-name">{sharedUser.name}</div>
                                <div className="user-email">{sharedUser.email}</div>
                              </div>
                              <div className="user-role-badge">
                                {sharedUser.role || t('member', 'Member')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="error-state">
                  <div className="error-icon">
                    <AlertTriangle size={48} />
                  </div>
                  <h4>{t('failedToLoadDetails', 'Failed to Load Details')}</h4>
                  <p>{t('unableToLoadDetails', 'Unable to load list details. Please try again.')}</p>
                  <button 
                    onClick={() => handleViewList(viewListModal.list?.id)}
                    className="neo-btn neo-btn-secondary"
                  >
                    <RefreshCw size={16} />
                    {t('retry', 'Retry')}
                  </button>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button onClick={closeViewListModal} className="neo-btn neo-btn-primary">
                <X size={16} />
                {t('close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modal */}
      {modal.show && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#333'
            }}>
              {modal.title}
            </h3>
            <p style={{
              margin: '0 0 2rem 0',
              color: '#666',
              lineHeight: 1.5,
              whiteSpace: 'pre-line'
            }}>
              {modal.message}
            </p>
            
            {/* Text input for delete account confirmation */}
            {modal.type === 'delete-account' && (
              <div style={{ marginBottom: '2rem' }}>
                <input
                  type="text"
                  value={deleteConfirmationText}
                  onChange={(e) => setDeleteConfirmationText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #dc2626',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    backgroundColor: '#fef2f2'
                  }}
                />
              </div>
            )}
            
            <div className="modal-actions" style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              {(modal.type === 'confirm' || modal.type === 'delete-account') && modal.onCancel && (
                <button
                  onClick={() => {
                    if (modal.type === 'delete-account') {
                      setDeleteConfirmationText('');
                    }
                    modal.onCancel();
                  }}
                  className="btn-secondary"
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #ddd',
                    backgroundColor: '#f8f9fa',
                    color: '#666',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  {t('cancel', 'Cancel')}
                </button>
              )}
              <button
                onClick={() => {
                  if (modal.type === 'delete-account') {
                    modal.onConfirm(deleteConfirmationText);
                    setDeleteConfirmationText('');
                  } else {
                    modal.onConfirm();
                  }
                }}
                disabled={modal.type === 'delete-account' && deleteConfirmationText.toUpperCase() !== 'DELETE'}
                className={modal.type === 'confirm' || modal.type === 'delete-account' ? 'btn-primary' : 'btn-secondary'}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  backgroundColor: modal.type === 'delete-account' 
                    ? (deleteConfirmationText.toUpperCase() === 'DELETE' ? '#dc2626' : '#9ca3af')
                    : (modal.type === 'confirm' ? '#667eea' : '#f8f9fa'),
                  color: modal.type === 'confirm' || modal.type === 'delete-account' ? '#fff' : '#666',
                  borderRadius: '8px',
                  cursor: (modal.type === 'delete-account' && deleteConfirmationText.toUpperCase() !== 'DELETE') ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: (modal.type === 'delete-account' && deleteConfirmationText.toUpperCase() !== 'DELETE') ? 0.6 : 1
                }}
              >
                {modal.type === 'delete-account' 
                  ? t('deleteAccount', 'Delete Account')
                  : (modal.type === 'confirm' ? t('confirm', 'Confirm') : t('ok', 'OK'))
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      <TransferOwnershipModal
        isOpen={transferOwnershipModal.show}
        onClose={closeTransferOwnershipModal}
        list={transferOwnershipModal.list}
        onTransfer={handleTransferComplete}
      />
    </div>
  );
};

export default SettingsPage;
